// src/components/SessionSaverDialog.js
import React, { useState, useEffect, cloneElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";

// Redux Actions
import {
  setTimeOfDay,
  setDartsUsed,
  setIsDrunk,
  setLocation,
  setFeeling,
  setBeerAmount,
  resetSession,
} from "../../../store/slices/sessionSlice";

// Firebase
import { db } from "../../../config/firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

// Child Components
import SessionForm from "./SessionForm";
// Headless UI
import { Dialog, DialogPanel } from "@headlessui/react";

// Input validation and sanitization helpers
const sanitizeString = (str) => {
  if (typeof str !== "string") return "";
  return str
    .trim()
    .slice(0, 100)
    .replace(/[<>\"'&]/g, "");
};

// Filter statistics to exclude checkout_stats and pro match results
const filterStatisticsForSaving = (statistics) => {
  if (!statistics || typeof statistics !== "object") {
    return {};
  }

  // Create a copy of statistics without checkout_stats and pro-specific data
  const filteredStats = { ...statistics };

  // Remove checkout_stats completely (these are large lookup tables)
  delete filteredStats.checkout_stats;

  // Remove pro match results (these are simulation data against pros)
  delete filteredStats.pro_match_results;

  // Remove any other pro-specific or temporary analysis data
  delete filteredStats.advanced_analytics;
  delete filteredStats.optimization_suggestions;
  delete filteredStats.tournament_simulations;
  delete filteredStats.match_predictions;

  // Keep only core user performance metrics like:
  // - scoring averages, hit rates, percentiles
  // - basic statistics that represent actual user performance
  // - deviation measurements, accuracy metrics

  return filteredStats;
};

// Calculate session summary from round history
const calculateSessionSummary = (roundHistory) => {
  if (!roundHistory || roundHistory.length === 0) {
    return {
      totalRounds: 0,
      totalDarts: 0,
      totalScore: 0,
      averagePerRound: 0,
      averagePerDart: 0,
      highestRound: 0,
    };
  }

  const totalRounds = roundHistory.length;
  const totalDarts = roundHistory.reduce(
    (acc, round) => acc + round.hits.length,
    0
  );
  const totalScore = roundHistory.reduce(
    (acc, round) => acc + round.totalScore,
    0
  );
  const highestRound = Math.max(
    ...roundHistory.map((round) => round.totalScore)
  );

  return {
    totalRounds,
    totalDarts,
    totalScore,
    averagePerRound:
      totalRounds > 0 ? parseFloat((totalScore / totalRounds).toFixed(2)) : 0,
    averagePerDart:
      totalDarts > 0 ? parseFloat((totalScore / totalDarts).toFixed(2)) : 0,
    highestRound,
  };
};

// Generate a unique session ID based on date and time
const generateSessionId = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const milliseconds = String(now.getMilliseconds()).padStart(3, "0");

  // Format: YYYY-MM-DD_HH-MM-SS-mmm (includes milliseconds for uniqueness)
  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}-${milliseconds}`;
};

const validateSessionData = (data) => {
  const errors = [];

  // if (!data.location || data.location.length === 0) {
  //   errors.push("Location is required");
  // }
  // if (!data.feeling || data.feeling.length === 0) {
  //   errors.push("Feeling is required");
  // }
  // if (data.beerAmount < 0 || data.beerAmount > 50) {
  //   errors.push("Beer amount must be between 0 and 50");
  // }
  // if (data.location && data.location.length > 100) {
  //   errors.push("Location must be less than 100 characters");
  // }
  // if (data.feeling && data.feeling.length > 100) {
  //   errors.push("Feeling must be less than 100 characters");
  // }

  return errors;
};

const SessionSaverDialog = ({ trigger = null }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isLoggedIn, userInfo } = useSelector((s) => s.user);
  const { timeOfDay, dartsUsed, isDrunk, location, feeling, beerAmount } =
    useSelector((s) => s.session);
  const statistics = useSelector((s) => s.dartGame.statistics);
  const roundHistory = useSelector((s) => s.dartGame.roundHistory);

  const [showSessionPopup, setShowSessionPopup] = useState(false);
  const [darts, setDarts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "info",
  });
  const [lastSaveTime, setLastSaveTime] = useState(null);

  // Rate limiting: minimum 10 seconds between saves
  const RATE_LIMIT_MS = 10000;

  // Show notification helper
  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "info" });
    }, 5000);
  };

  // Check if save is currently rate limited
  const isSaveRateLimited = () => {
    if (!lastSaveTime) return false;
    return Date.now() - lastSaveTime < RATE_LIMIT_MS;
  };

  // Get remaining time for rate limit
  const getRateLimitRemainingTime = () => {
    if (!lastSaveTime) return 0;
    const remaining = RATE_LIMIT_MS - (Date.now() - lastSaveTime);
    return Math.max(0, Math.ceil(remaining / 1000));
  };

  // initialize timeOfDay
  useEffect(() => {
    const currentTime = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    dispatch(setTimeOfDay(currentTime));
  }, [dispatch]);

  // subscribe to darts collection - using uid instead of email
  useEffect(() => {
    if (!userInfo?.uid) return;

    const dartsCollection = collection(db, "userInfo", userInfo.uid, "darts");
    const q = query(dartsCollection, orderBy("timestamp", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const loaded = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDarts(loaded);
      },
      (err) => {
        console.error("Error loading darts:", err.code || "unknown_error");
        showNotification(t("sessionDialog.loadError"), "error");
      }
    );
    return () => unsubscribe();
  }, [userInfo, t]);

  // save session to Firestore with rate limiting and security improvements
  const handleSaveSession = async () => {
    // Check authentication
    if (!userInfo?.uid) {
      showNotification(t("sessionDialog.noUserFound"), "error");
      return;
    }

    // Check if already loading
    if (isLoading) {
      showNotification(
        t("sessionDialog.saveInProgress") || "Save in progress...",
        "info"
      );
      return;
    }

    // Rate limiting check
    if (isSaveRateLimited()) {
      const remainingSeconds = getRateLimitRemainingTime();
      showNotification(
        t("sessionDialog.rateLimitError", { seconds: remainingSeconds }) ||
          `Please wait ${remainingSeconds} seconds before saving again`,
        "warning"
      );
      return;
    }

    // Validate and sanitize input data
    const sessionData = {
      timeOfDay: sanitizeString(timeOfDay),
      isDrunk: Boolean(isDrunk),
      location: sanitizeString(location),
      feeling: sanitizeString(feeling),
      beerAmount: Math.max(0, Math.min(50, Number(beerAmount) || 0)),
    };

    const validationErrors = validateSessionData(sessionData);
    if (validationErrors.length > 0) {
      showNotification(validationErrors.join(", "), "error");
      return;
    }

    // Set loading state and record save attempt time
    setIsLoading(true);
    const saveStartTime = Date.now();
    setLastSaveTime(saveStartTime);

    try {
      let dartRef = null;
      if (dartsUsed) {
        dartRef = doc(db, "userInfo", userInfo.uid, "darts", dartsUsed);
      }

      // Filter statistics to exclude checkout_stats and pro match results
      const filteredStatistics = filterStatisticsForSaving(statistics);

      // Prepare roundHistory data for saving
      const roundHistoryForSaving = roundHistory.map((round) => ({
        hits: round.hits.map((hit) => ({
          label: hit.label,
          score: hit.score,
          x: hit.x,
          y: hit.y,
        })),
        totalScore: round.totalScore,
      }));

      // Calculate session summary from round history
      const sessionSummary = calculateSessionSummary(roundHistory);

      // Generate a unique session ID based on date and time
      const sessionId = generateSessionId();

      // Use setDoc with custom ID instead of addDoc
      const sessionRef = doc(
        db,
        "userInfo",
        userInfo.uid,
        "sessions",
        sessionId
      );
      await setDoc(sessionRef, {
        ...sessionData,
        dartsUsed: dartRef || "",
        createdAt: serverTimestamp(),
        statistics: filteredStatistics,
        roundHistory: roundHistoryForSaving,
        sessionSummary: sessionSummary,
      });

      showNotification(t("sessionDialog.saveSuccess"), "success");
      dispatch(resetSession());
      setShowSessionPopup(false);
    } catch (error) {
      // Reset rate limit on error to allow retry
      setLastSaveTime(null);

      // Show specific error messages for known Firebase error codes
      let errorMessage = t("sessionDialog.saveError");
      if (error.code === "permission-denied") {
        errorMessage =
          t("sessionDialog.permissionError") ||
          "You don't have permission to save sessions";
      } else if (error.code === "quota-exceeded") {
        errorMessage =
          t("sessionDialog.quotaError") || "Storage quota exceeded";
      } else if (error.code === "network-request-failed") {
        errorMessage =
          t("sessionDialog.networkError") ||
          "Network error, please check your connection";
      }

      showNotification(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoggedIn) return null;

  const openPopup = () => setShowSessionPopup(true);
  const triggerElement = trigger ? (
    cloneElement(trigger, { onClick: openPopup })
  ) : (
    <button
      onClick={openPopup}
      className="inline-block bg-test hover:bg-secondary text-white py-2 px-7 rounded-full shadow transition duration-200"
      disabled={isLoading}
    >
      {isLoading
        ? t("sessionDialog.saving") || "Saving..."
        : t("sessionDialog.save")}
    </button>
  );

  return (
    <div>
      {/* Trigger Button */}
      {triggerElement}

      {/* Notification */}
      {notification.show && (
        <div
          className={`fixed top-4 right-4 z-60 p-4 rounded-md shadow-lg ${
            notification.type === "error"
              ? "bg-red-500 text-white"
              : notification.type === "success"
              ? "bg-green-500 text-white"
              : notification.type === "warning"
              ? "bg-yellow-500 text-black"
              : "bg-blue-500 text-white"
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Modal */}
      <Dialog
        open={showSessionPopup}
        onClose={() => setShowSessionPopup(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen bg-black bg-opacity-50 p-4">
          <DialogPanel className="relative w-full max-w-xl rounded  p-6 shadow-lg">
            {/* Close "X" */}
            <button
              onClick={() => setShowSessionPopup(false)}
              className="absolute top-6 right-6 p-2 m-2 rounded text-gray-400 hover:text-test"
              disabled={isLoading}
            >
              <FaTimes className="w-5 h-5 " />
            </button>

            {/* Form */}
            <SessionForm
              timeOfDay={timeOfDay}
              dartsUsed={dartsUsed}
              isDrunk={isDrunk}
              location={location}
              feeling={feeling}
              beerAmount={beerAmount}
              darts={darts}
              onSaveSession={handleSaveSession}
              dispatch={dispatch}
              setTimeOfDay={setTimeOfDay}
              setDartsUsed={setDartsUsed}
              setIsDrunk={setIsDrunk}
              setLocation={setLocation}
              setFeeling={setFeeling}
              setBeerAmount={setBeerAmount}
              isLoading={isLoading}
              parentIsLoading={isLoading}
              isSaveRateLimited={isSaveRateLimited()}
              rateLimitRemainingTime={getRateLimitRemainingTime()}
            />
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
};

export default SessionSaverDialog;
